// src/pages/Campaigns/SurveyGenie/DataCleaningDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, Grid, Card, CardContent, 
    Chip, IconButton, Tooltip, CircularProgress, Alert,
    LinearProgress, Tabs, Tab, Divider, Badge, Avatar,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, MenuItem, Switch,
    FormControlLabel, Slider, TextField, Stack
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Refresh as RefreshIcon,
    PlayArrow as RunIcon,
    Settings as SettingsIcon,
    CheckCircle as ValidIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    FilterList as FilterIcon,
    CleaningServices as CleanIcon,
    Timeline as TrendIcon,
    BugReport as IssueIcon,
    Rule as RuleIcon,
    History as HistoryIcon,
    Speed as SpeedIcon,
    ContentCopy as DuplicateIcon,
    Assignment as IncompleteIcon,
    TrendingUp as TrendingUpIcon,
    Visibility as ViewIcon,
    Archive as ArchiveIcon,
    Delete as DeleteIcon,
    Undo as UndoIcon,
    AutoFixHigh as AutoIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip, Legend, ResponsiveContainer, LineChart, Line, Area, AreaChart
} from 'recharts';
import axiosInstance from '../../../utils/axiosInstance';

// Color palette
const COLORS = {
    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    info: '#2196f3',
    primary: '#1976d2',
    secondary: '#9c27b0'
};

const ISSUE_TYPE_COLORS = {
    INCOMPLETE_DETECTION: '#ff9800',
    DUPLICATE_DETECTION: '#9c27b0',
    OUTLIER_DETECTION: '#f44336',
    SPEEDER_DETECTION: '#e91e63',
    FLATLINE_DETECTION: '#673ab7',
    PATTERN_VALIDATION: '#3f51b5',
    CONSISTENCY_CHECK: '#00bcd4',
    GIBBERISH_DETECTION: '#795548'
};

const ISSUE_TYPE_ICONS = {
    INCOMPLETE_DETECTION: <IncompleteIcon />,
    DUPLICATE_DETECTION: <DuplicateIcon />,
    OUTLIER_DETECTION: <TrendingUpIcon />,
    SPEEDER_DETECTION: <SpeedIcon />,
    FLATLINE_DETECTION: <TrendIcon />,
};

// Quality Score Gauge Component
const QualityScoreGauge = ({ score }) => {
    const getColor = () => {
        if (score >= 80) return COLORS.success;
        if (score >= 60) return COLORS.warning;
        return COLORS.error;
    };

    const getLabel = () => {
        if (score >= 80) return 'Good';
        if (score >= 60) return 'Fair';
        return 'Poor';
    };

    return (
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
                variant="determinate"
                value={100}
                size={120}
                thickness={4}
                sx={{ color: 'grey.200' }}
            />
            <CircularProgress
                variant="determinate"
                value={score}
                size={120}
                thickness={4}
                sx={{ 
                    color: getColor(),
                    position: 'absolute',
                    left: 0,
                }}
            />
            <Box
                sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography variant="h4" fontWeight={700} color={getColor()}>
                    {Math.round(score)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {getLabel()}
                </Typography>
            </Box>
        </Box>
    );
};

// Issue Summary Card
const IssueSummaryCard = ({ type, count, icon, color, onClick }) => (
    <Card 
        sx={{ 
            cursor: 'pointer', 
            transition: 'all 0.2s',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
            borderLeft: `4px solid ${color}`
        }}
        onClick={onClick}
    >
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: `${color}20`, color: color }}>
                {icon}
            </Avatar>
            <Box>
                <Typography variant="h5" fontWeight={600}>{count}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {type.replace('_', ' ').replace('DETECTION', '').trim()}
                </Typography>
            </Box>
        </CardContent>
    </Card>
);

// Detection Settings Dialog
const DetectionSettingsDialog = ({ open, onClose, settings, onSave }) => {
    const [localSettings, setLocalSettings] = useState(settings);

    useEffect(() => {
        setLocalSettings(settings);
    }, [settings]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Detection Settings</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    <Box>
                        <Typography gutterBottom>
                            Minimum Completion Percentage: {localSettings.min_completion_percentage}%
                        </Typography>
                        <Slider
                            value={localSettings.min_completion_percentage}
                            onChange={(e, v) => setLocalSettings({...localSettings, min_completion_percentage: v})}
                            min={0}
                            max={100}
                            marks={[{value: 50, label: '50%'}, {value: 80, label: '80%'}]}
                        />
                    </Box>
                    
                    <Box>
                        <Typography gutterBottom>
                            Min Seconds Per Question: {localSettings.min_seconds_per_question}s
                        </Typography>
                        <Slider
                            value={localSettings.min_seconds_per_question}
                            onChange={(e, v) => setLocalSettings({...localSettings, min_seconds_per_question: v})}
                            min={1}
                            max={10}
                            marks={[{value: 2, label: '2s'}, {value: 5, label: '5s'}]}
                        />
                    </Box>

                    <Box>
                        <Typography gutterBottom>
                            Outlier Z-Score Threshold: {localSettings.outlier_zscore_threshold}
                        </Typography>
                        <Slider
                            value={localSettings.outlier_zscore_threshold}
                            onChange={(e, v) => setLocalSettings({...localSettings, outlier_zscore_threshold: v})}
                            min={1}
                            max={5}
                            step={0.5}
                            marks={[{value: 2, label: '2σ'}, {value: 3, label: '3σ'}]}
                        />
                    </Box>

                    <Divider />

                    <FormControlLabel
                        control={
                            <Switch 
                                checked={localSettings.detect_incomplete}
                                onChange={(e) => setLocalSettings({...localSettings, detect_incomplete: e.target.checked})}
                            />
                        }
                        label="Detect Incomplete Responses"
                    />
                    <FormControlLabel
                        control={
                            <Switch 
                                checked={localSettings.detect_duplicates}
                                onChange={(e) => setLocalSettings({...localSettings, detect_duplicates: e.target.checked})}
                            />
                        }
                        label="Detect Duplicates"
                    />
                    <FormControlLabel
                        control={
                            <Switch 
                                checked={localSettings.detect_speeders}
                                onChange={(e) => setLocalSettings({...localSettings, detect_speeders: e.target.checked})}
                            />
                        }
                        label="Detect Speeders"
                    />
                    <FormControlLabel
                        control={
                            <Switch 
                                checked={localSettings.detect_outliers}
                                onChange={(e) => setLocalSettings({...localSettings, detect_outliers: e.target.checked})}
                            />
                        }
                        label="Detect Outliers"
                    />
                    <FormControlLabel
                        control={
                            <Switch 
                                checked={localSettings.detect_flatliners}
                                onChange={(e) => setLocalSettings({...localSettings, detect_flatliners: e.target.checked})}
                            />
                        }
                        label="Detect Flatline Patterns"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={() => { onSave(localSettings); onClose(); }}>
                    Save Settings
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Main Component
const DataCleaningDashboard = () => {
    const { chatbotId, surveyId } = useParams();
    const navigate = useNavigate();

    // State
    const [loading, setLoading] = useState(true);
    const [detecting, setDetecting] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [dashboardData, setDashboardData] = useState(null);
    const [detectedIssues, setDetectedIssues] = useState([]);
    const [trendData, setTrendData] = useState([]);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Detection settings
    const [detectionSettings, setDetectionSettings] = useState({
        detect_incomplete: true,
        detect_duplicates: true,
        detect_outliers: true,
        detect_speeders: true,
        detect_flatliners: true,
        min_completion_percentage: 80,
        min_seconds_per_question: 2,
        outlier_zscore_threshold: 3
    });

    // Fetch dashboard data
    const fetchDashboard = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/api/surveys/cleaning/dashboard', {
                params: { survey_id: surveyId }
            });
            if (response.data.success) {
                setDashboardData(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching dashboard:', error);
        } finally {
            setLoading(false);
        }
    }, [surveyId]);

    // Fetch trend data
    const fetchTrend = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/surveys/cleaning/metrics/history', {
                params: { survey_id: surveyId, days: 30 }
            });
            if (response.data.success) {
                setTrendData(response.data.data.trend);
            }
        } catch (error) {
            console.error('Error fetching trend:', error);
        }
    }, [surveyId]);

    useEffect(() => {
        fetchDashboard();
        fetchTrend();
    }, [fetchDashboard, fetchTrend]);

    // Run detection
    const runDetection = async () => {
        setDetecting(true);
        try {
            const response = await axiosInstance.post('/api/surveys/cleaning/detect', detectionSettings, {
                params: { survey_id: surveyId }
            });
            if (response.data.success) {
                setDetectedIssues(response.data.data.issues);
                setSnackbar({
                    open: true,
                    message: `Found ${response.data.data.total_issues} issues`,
                    severity: 'info'
                });
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'Detection failed', severity: 'error' });
        } finally {
            setDetecting(false);
        }
    };

    // Save detected issues
    const saveIssues = async () => {
        try {
            const response = await axiosInstance.post('/api/surveys/cleaning/issues/save', 
                { issues: detectedIssues },
                { params: { survey_id: surveyId } }
            );
            if (response.data.success) {
                setSnackbar({
                    open: true,
                    message: `${response.data.data.saved_count} issues saved`,
                    severity: 'success'
                });
                setDetectedIssues([]);
                fetchDashboard();
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to save issues', severity: 'error' });
        }
    };

    // Apply bulk action
    const applyBulkAction = async (action, options = {}) => {
        try {
            const response = await axiosInstance.post('/api/surveys/cleaning/apply', 
                { action, options, preview_only: false },
                { params: { survey_id: surveyId } }
            );
            if (response.data.success) {
                setSnackbar({
                    open: true,
                    message: `${response.data.data.affected_count} responses affected`,
                    severity: 'success'
                });
                fetchDashboard();
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'Action failed', severity: 'error' });
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    const { overview, issues_summary, quality_indicators, recent_activity } = dashboardData || {};

    // Prepare chart data
    const issuesPieData = issues_summary?.issues_by_type 
        ? Object.entries(issues_summary.issues_by_type).map(([type, count]) => ({
            name: type.replace('_DETECTION', '').replace('_', ' '),
            value: count,
            color: ISSUE_TYPE_COLORS[type] || COLORS.primary
        }))
        : [];

    return (
        <Box sx={{ p: 3, mt: 10 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/surveys`)}>
                        <BackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>
                            <CleanIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Data Cleaning Tools
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Detect and resolve data quality issues
                        </Typography>
                    </Box>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        startIcon={<SettingsIcon />}
                        onClick={() => setSettingsOpen(true)}
                    >
                        Settings
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={detecting ? <CircularProgress size={20} color="inherit" /> : <RunIcon />}
                        onClick={runDetection}
                        disabled={detecting}
                    >
                        {detecting ? 'Detecting...' : 'Run Detection'}
                    </Button>
                </Stack>
            </Box>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                    <Tab icon={<TrendIcon />} label="Dashboard" iconPosition="start" />
                    <Tab 
                        icon={
                            <Badge badgeContent={issues_summary?.open_issues || 0} color="error">
                                <IssueIcon />
                            </Badge>
                        } 
                        label="Issues" 
                        iconPosition="start" 
                    />
                    <Tab icon={<RuleIcon />} label="Rules" iconPosition="start" />
                    <Tab icon={<HistoryIcon />} label="History" iconPosition="start" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && (
                <Grid container spacing={3}>
                    {/* Quality Score */}
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3, height: '100%', textAlign: 'center' }}>
                            <Typography variant="h6" gutterBottom>Data Quality Score</Typography>
                            <QualityScoreGauge score={overview?.data_quality_score || 0} />
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Based on completeness, duplicates, and validation
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Key Metrics */}
                    <Grid item xs={12} md={8}>
                        <Paper sx={{ p: 3, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>Key Metrics</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={3}>
                                    <Box sx={{ textAlign: 'center', p: 2 }}>
                                        <Typography variant="h4" fontWeight={600} color="primary">
                                            {overview?.total_responses || 0}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Total Responses
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Box sx={{ textAlign: 'center', p: 2 }}>
                                        <Typography variant="h4" fontWeight={600} color="success.main">
                                            {overview?.completion_rate || 0}%
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Completion Rate
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Box sx={{ textAlign: 'center', p: 2 }}>
                                        <Typography variant="h4" fontWeight={600} color="warning.main">
                                            {quality_indicators?.duplicate_rate || 0}%
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Duplicate Rate
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Box sx={{ textAlign: 'center', p: 2 }}>
                                        <Typography variant="h4" fontWeight={600} color="error.main">
                                            {quality_indicators?.speeder_rate || 0}%
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Speeder Rate
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Chip
                                    icon={<ValidIcon />}
                                    label={`${quality_indicators?.completeness || 0}% Complete`}
                                    color="success"
                                    variant="outlined"
                                />
                                <Chip
                                    icon={<WarningIcon />}
                                    label={`${issues_summary?.open_issues || 0} Open Issues`}
                                    color={issues_summary?.open_issues > 0 ? 'warning' : 'default'}
                                    variant="outlined"
                                />
                                <Chip
                                    icon={<DuplicateIcon />}
                                    label={`${issues_summary?.potential_duplicates || 0} Duplicates`}
                                    color={issues_summary?.potential_duplicates > 0 ? 'secondary' : 'default'}
                                    variant="outlined"
                                />
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Issues by Type */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>Issues by Type</Typography>
                            {issuesPieData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={250}>
                                    <PieChart>
                                        <Pie
                                            data={issuesPieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={50}
                                            outerRadius={80}
                                            paddingAngle={2}
                                            dataKey="value"
                                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        >
                                            {issuesPieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <ValidIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                                    <Typography color="text.secondary">No issues detected</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Quality Trend */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>Response Trend (30 Days)</Typography>
                            {trendData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={250}>
                                    <AreaChart data={trendData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis 
                                            dataKey="date" 
                                            tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        />
                                        <YAxis />
                                        <RechartsTooltip />
                                        <Area 
                                            type="monotone" 
                                            dataKey="total_responses" 
                                            stroke={COLORS.primary}
                                            fill={COLORS.primary}
                                            fillOpacity={0.3}
                                            name="Total"
                                        />
                                        <Area 
                                            type="monotone" 
                                            dataKey="completed_responses" 
                                            stroke={COLORS.success}
                                            fill={COLORS.success}
                                            fillOpacity={0.3}
                                            name="Completed"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography color="text.secondary">No trend data available</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Quick Actions */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>Quick Actions</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<IncompleteIcon />}
                                        onClick={() => applyBulkAction('archive_incomplete', { min_completion_percentage: 80 })}
                                        sx={{ py: 2 }}
                                    >
                                        Archive Incomplete ({issues_summary?.incomplete_responses || 0})
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<DuplicateIcon />}
                                        onClick={() => applyBulkAction('remove_duplicates', { keep_rule: 'most_complete' })}
                                        sx={{ py: 2 }}
                                    >
                                        Remove Duplicates ({issues_summary?.potential_duplicates || 0})
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<SpeedIcon />}
                                        onClick={() => applyBulkAction('archive_speeders', { min_total_seconds: 60 })}
                                        sx={{ py: 2 }}
                                    >
                                        Archive Speeders ({issues_summary?.speeders || 0})
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<IssueIcon />}
                                        onClick={() => setActiveTab(1)}
                                        sx={{ py: 2 }}
                                    >
                                        Review All Issues
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Detected Issues Preview */}
                    {detectedIssues.length > 0 && (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6">
                                        Detected Issues ({detectedIssues.length})
                                    </Typography>
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => setDetectedIssues([])}
                                        >
                                            Clear
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={<ValidIcon />}
                                            onClick={saveIssues}
                                        >
                                            Save Issues
                                        </Button>
                                    </Stack>
                                </Box>
                                <TableContainer sx={{ maxHeight: 400 }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Type</TableCell>
                                                <TableCell>Title</TableCell>
                                                <TableCell>Severity</TableCell>
                                                <TableCell>Response ID</TableCell>
                                                <TableCell>Details</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {detectedIssues.slice(0, 50).map((issue, idx) => (
                                                <TableRow key={idx} hover>
                                                    <TableCell>
                                                        <Chip 
                                                            size="small"
                                                            label={issue.type.replace('_DETECTION', '')}
                                                            sx={{ 
                                                                bgcolor: `${ISSUE_TYPE_COLORS[issue.type]}20`,
                                                                color: ISSUE_TYPE_COLORS[issue.type]
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{issue.title}</TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            size="small"
                                                            label={issue.severity}
                                                            color={
                                                                issue.severity === 'HIGH' ? 'error' :
                                                                issue.severity === 'MEDIUM' ? 'warning' : 'default'
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>#{issue.response_id}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {JSON.stringify(issue.details).substring(0, 50)}...
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {detectedIssues.length > 50 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        Showing 50 of {detectedIssues.length} issues
                                    </Typography>
                                )}
                            </Paper>
                        </Grid>
                    )}

                    {/* Recent Activity */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>Recent Cleaning Activity</Typography>
                            {recent_activity && recent_activity.length > 0 ? (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Action</TableCell>
                                                <TableCell>Description</TableCell>
                                                <TableCell>Records Affected</TableCell>
                                                <TableCell>Date</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {recent_activity.map((log) => (
                                                <TableRow key={log.id} hover>
                                                    <TableCell>
                                                        <Chip size="small" label={log.action_type} />
                                                    </TableCell>
                                                    <TableCell>{log.action_description}</TableCell>
                                                    <TableCell>{log.records_affected}</TableCell>
                                                    <TableCell>
                                                        {new Date(log.executed_at).toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                    No recent cleaning activity
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {activeTab === 1 && (
                <DataQualityIssuesTab surveyId={surveyId} onRefresh={fetchDashboard} />
            )}

            {activeTab === 2 && (
                <CleaningRulesTab surveyId={surveyId} />
            )}

            {activeTab === 3 && (
                <CleaningHistoryTab surveyId={surveyId} />
            )}

            {/* Settings Dialog */}
            <DetectionSettingsDialog
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                settings={detectionSettings}
                onSave={setDetectionSettings}
            />
        </Box>
    );
};

// Issues Tab Component
const DataQualityIssuesTab = ({ surveyId, onRefresh }) => {
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [statusFilter, setStatusFilter] = useState('PENDING');
    const [typeFilter, setTypeFilter] = useState('');
    const [selectedIssues, setSelectedIssues] = useState([]);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedIssue, setSelectedIssue] = useState(null);

    const fetchIssues = useCallback(async () => {
        setLoading(true);
        try {
            const params = { survey_id: surveyId, page, per_page: 20 };
            if (statusFilter) params.status = statusFilter;
            if (typeFilter) params.issue_type = typeFilter;

            const response = await axiosInstance.get('/api/surveys/cleaning/issues', { params });
            if (response.data.success) {
                setIssues(response.data.data);
                setTotalPages(response.data.pagination.pages);
            }
        } catch (error) {
            console.error('Error fetching issues:', error);
        } finally {
            setLoading(false);
        }
    }, [surveyId, page, statusFilter, typeFilter]);

    useEffect(() => {
        fetchIssues();
    }, [fetchIssues]);

    const resolveIssue = async (issueId, action) => {
        try {
            await axiosInstance.post('/api/surveys/cleaning/issues/resolve', 
                { action },
                { params: { issue_id: issueId } }
            );
            fetchIssues();
            onRefresh();
        } catch (error) {
            console.error('Error resolving issue:', error);
        }
    };

    const bulkResolve = async (action) => {
        if (selectedIssues.length === 0) return;
        try {
            await axiosInstance.post('/api/surveys/cleaning/issues/bulk-resolve', {
                issue_ids: selectedIssues,
                action
            });
            setSelectedIssues([]);
            fetchIssues();
            onRefresh();
        } catch (error) {
            console.error('Error bulk resolving:', error);
        }
    };

    const viewDetails = async (issue) => {
        try {
            const response = await axiosInstance.get('/api/surveys/cleaning/issues/details', {
                params: { issue_id: issue.id }
            });
            if (response.data.success) {
                setSelectedIssue(response.data.data);
                setDetailsOpen(true);
            }
        } catch (error) {
            console.error('Error fetching details:', error);
        }
    };

    return (
        <Box>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="PENDING">Pending</MenuItem>
                        <MenuItem value="REVIEWED">Reviewed</MenuItem>
                        <MenuItem value="RESOLVED">Resolved</MenuItem>
                        <MenuItem value="DISMISSED">Dismissed</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Type</InputLabel>
                    <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
                        <MenuItem value="">All Types</MenuItem>
                        <MenuItem value="INCOMPLETE_DETECTION">Incomplete</MenuItem>
                        <MenuItem value="DUPLICATE_DETECTION">Duplicate</MenuItem>
                        <MenuItem value="OUTLIER_DETECTION">Outlier</MenuItem>
                        <MenuItem value="SPEEDER_DETECTION">Speeder</MenuItem>
                        <MenuItem value="FLATLINE_DETECTION">Flatline</MenuItem>
                    </Select>
                </FormControl>

                <Box sx={{ flex: 1 }} />

                {selectedIssues.length > 0 && (
                    <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" onClick={() => bulkResolve('dismiss')}>
                            Dismiss ({selectedIssues.length})
                        </Button>
                        <Button size="small" variant="outlined" color="warning" onClick={() => bulkResolve('archive')}>
                            Archive ({selectedIssues.length})
                        </Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => bulkResolve('delete')}>
                            Delete ({selectedIssues.length})
                        </Button>
                    </Stack>
                )}

                <IconButton onClick={fetchIssues}>
                    <RefreshIcon />
                </IconButton>
            </Paper>

            {/* Issues Table */}
            <Paper>
                {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <CircularProgress />
                    </Box>
                ) : issues.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <ValidIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                        <Typography>No issues found</Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedIssues.length === issues.length}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedIssues(issues.map(i => i.id));
                                                } else {
                                                    setSelectedIssues([]);
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Title</TableCell>
                                    <TableCell>Severity</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Detected</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {issues.map((issue) => (
                                    <TableRow key={issue.id} hover>
                                        <TableCell padding="checkbox">
                                            <input
                                                type="checkbox"
                                                checked={selectedIssues.includes(issue.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedIssues([...selectedIssues, issue.id]);
                                                    } else {
                                                        setSelectedIssues(selectedIssues.filter(id => id !== issue.id));
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                icon={ISSUE_TYPE_ICONS[issue.issue_type] || <IssueIcon />}
                                                label={issue.issue_type.replace('_DETECTION', '')}
                                                sx={{
                                                    bgcolor: `${ISSUE_TYPE_COLORS[issue.issue_type] || COLORS.primary}20`,
                                                    color: ISSUE_TYPE_COLORS[issue.issue_type] || COLORS.primary
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{issue.title}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Response #{issue.response_id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={issue.severity}
                                                color={
                                                    issue.severity === 'CRITICAL' ? 'error' :
                                                    issue.severity === 'HIGH' ? 'error' :
                                                    issue.severity === 'MEDIUM' ? 'warning' : 'default'
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={issue.status}
                                                variant="outlined"
                                                color={
                                                    issue.status === 'RESOLVED' ? 'success' :
                                                    issue.status === 'PENDING' ? 'warning' : 'default'
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {new Date(issue.detected_at).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="View Details">
                                                <IconButton size="small" onClick={() => viewDetails(issue)}>
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                            {issue.status === 'PENDING' && (
                                                <>
                                                    <Tooltip title="Archive Response">
                                                        <IconButton size="small" onClick={() => resolveIssue(issue.id, 'archive')}>
                                                            <ArchiveIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Dismiss Issue">
                                                        <IconButton size="small" onClick={() => resolveIssue(issue.id, 'dismiss')}>
                                                            <ValidIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Issue Details Dialog */}
            <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Issue Details
                    {selectedIssue?.issue && (
                        <Chip
                            size="small"
                            label={selectedIssue.issue.status}
                            sx={{ ml: 2 }}
                            color={selectedIssue.issue.status === 'PENDING' ? 'warning' : 'success'}
                        />
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    {selectedIssue && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Alert severity={
                                    selectedIssue.issue.severity === 'HIGH' ? 'error' :
                                    selectedIssue.issue.severity === 'MEDIUM' ? 'warning' : 'info'
                                }>
                                    <Typography variant="subtitle1">{selectedIssue.issue.title}</Typography>
                                    <Typography variant="body2">{selectedIssue.issue.description}</Typography>
                                </Alert>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>Detection Details</Typography>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <pre style={{ margin: 0, fontSize: 12, overflow: 'auto' }}>
                                        {JSON.stringify(selectedIssue.issue.detection_details, null, 2)}
                                    </pre>
                                </Paper>
                            </Grid>

                            {selectedIssue.response && (
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>Response Data</Typography>
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="body2">
                                            <strong>ID:</strong> #{selectedIssue.response.id}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Email:</strong> {selectedIssue.response.respondent_email || 'Anonymous'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Status:</strong> {selectedIssue.response.status}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Completed:</strong> {
                                                selectedIssue.response.completed_at 
                                                    ? new Date(selectedIssue.response.completed_at).toLocaleString()
                                                    : 'Not completed'
                                            }
                                        </Typography>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsOpen(false)}>Close</Button>
                    {selectedIssue?.issue?.status === 'PENDING' && (
                        <>
                            <Button 
                                color="inherit"
                                onClick={() => { resolveIssue(selectedIssue.issue.id, 'dismiss'); setDetailsOpen(false); }}
                            >
                                Dismiss
                            </Button>
                            <Button 
                                color="warning"
                                onClick={() => { resolveIssue(selectedIssue.issue.id, 'archive'); setDetailsOpen(false); }}
                            >
                                Archive Response
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// Placeholder for Rules Tab
const CleaningRulesTab = ({ surveyId }) => (
    <Paper sx={{ p: 4, textAlign: 'center' }}>
        <RuleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>Automated Cleaning Rules</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
            Create rules to automatically detect and handle data quality issues
        </Typography>
        <Button variant="contained" startIcon={<RuleIcon />}>
            Create Rule
        </Button>
    </Paper>
);

// Placeholder for History Tab
const CleaningHistoryTab = ({ surveyId }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const response = await axiosInstance.get('/api/surveys/cleaning/logs', {
                    params: { survey_id: surveyId, per_page: 50 }
                });
                if (response.data.success) {
                    setLogs(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching logs:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, [surveyId]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Paper>
            {logs.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography>No cleaning history yet</Typography>
                </Box>
            ) : (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Action</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Records Affected</TableCell>
                                <TableCell>Reversible</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.map((log) => (
                                <TableRow key={log.id} hover>
                                    <TableCell>
                                        {new Date(log.executed_at).toLocaleString()}
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={log.action_type} />
                                    </TableCell>
                                    <TableCell>{log.action_description}</TableCell>
                                    <TableCell>{log.records_affected}</TableCell>
                                    <TableCell>
                                        {log.is_reversible ? (
                                            log.reversed_at ? (
                                                <Chip size="small" label="Reversed" color="default" />
                                            ) : (
                                                <Chip size="small" label="Yes" color="success" variant="outlined" />
                                            )
                                        ) : (
                                            <Chip size="small" label="No" color="error" variant="outlined" />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {log.is_reversible && !log.reversed_at && (
                                            <Tooltip title="Undo Action">
                                                <IconButton size="small">
                                                    <UndoIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
};

export default DataCleaningDashboard;